/**
 * SavedItems — dedicated full-screen view of every save the signed-in user has
 * (concepts AND saved Oracle correlations), pulled from /api/saves/me which
 * already returns the normalised "save" shape used by the Settings → You list.
 *
 * Surfaces an "Add to collection" affordance on each row so users can file
 * items into shelves without leaving the page.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BookMarked, Globe, Lock, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import LoginPrompt from "../components/LoginPrompt";
import AddToCollectionMenu from "../components/AddToShelfMenu";
import CorrelationModal from "../components/CorrelationModal";
import ShareCardModal from "../components/ShareCardModal";
import { isRenderableCardData } from "../components/WeeklyItemCardThumb";

export default function SavedItems() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [modalItem, setModalItem] = useState(null);
  const [modalConceptLink, setModalConceptLink] = useState(null);
  const [modalWeeklyLink, setModalWeeklyLink] = useState(null);
  // Share-card target: which item the user is currently sharing as a card.
  const [shareItem, setShareItem] = useState(null);
  const [publishingId, setPublishingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/saves/me");
      setItems(data || []);
    } catch (err) {
      console.error("saves fetch failed", err);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    fetchItems();
  }, [user, loading, fetchItems]);

  const openItem = (it) => {
    const raw = it.raw || {};
    if (it.kind === "concept") {
      setModalItem({
        title: it.title,
        bullets: [],
        response: raw.note || raw.concept_summary || it.preview || "",
        visibility: it.visibility,
        created_at: it.created_at,
      });
      setModalConceptLink(`/concept/${it.concept_id}`);
      setModalWeeklyLink(null);
    } else {
      const itemId = it.save_id || it.id || null;
      setModalItem({
        id: itemId,
        title: it.title,
        caption: it.caption || raw.caption || "",
        bullets: it.bullets || raw.bullets || [],
        response: raw.response || it.preview || "",
        visibility: it.visibility,
        created_at: it.created_at,
        bumps_count: it.bumps_count,
        views_count: it.views_count,
        reactions_count: it.reactions_count,
        source_type: raw.source_type,
        user_handle: raw.user_handle,
        user_name: raw.user_name,
        card_data: raw.card_data,
      });
      setModalConceptLink(null);
      setModalWeeklyLink(it.visibility === "public" && itemId ? `/weekly#item-${itemId}` : null);
    }
  };

  const removeItem = async (it, ev) => {
    if (ev) ev.stopPropagation();
    try {
      if (it.kind === "concept") {
        await api.delete(`/pins/${it.concept_id || it.save_id}`);
      } else {
        await api.delete(`/oracle/saves/${it.save_id}`);
      }
      setItems((cur) => cur.filter((x) => x.save_id !== it.save_id));
      toast.success("Removed.");
    } catch { toast.error("Could not remove."); }
  };

  // Open the share-card modal for a saved correlation. If the save is still
  // private, we publish it first — the card's QR code needs a real Weekly
  // URL to point at, so choosing "Share as card" is an implicit opt-in to
  // making the correlation public.
  const openShareCard = async (it) => {
    if (it.kind === "concept") return;
    // Already public — just open the modal.
    if (it.visibility === "public") {
      setShareItem(it);
      return;
    }
    setPublishingId(it.save_id);
    try {
      const { data } = await api.post(`/oracle/saves/${it.save_id}/publish`);
      const nextRaw = { ...(it.raw || {}), ...(data?.save || {}), visibility: "public" };
      const nextItem = { ...it, visibility: "public", raw: nextRaw };
      setItems((cur) => cur.map((x) => (x.save_id === it.save_id ? nextItem : x)));
      if (!data?.already_public) {
        toast.success("Published to Weekly — your card can now be shared.");
      }
      setShareItem(nextItem);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not publish. Try again.");
    } finally {
      setPublishingId(null);
    }
  };

  if (loading) return null;
  if (!user) {
    return (
      <LoginPrompt
        title="Your saved items, waiting."
        blurb="Sign in to keep concepts, Oracle responses, and your own correlations in one place."
      />
    );
  }

  return (
    <div data-testid="saves-page" className="min-h-[calc(100vh-80px)]">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-16 sm:py-24 pb-32">
        <header className="mb-12">
          <button
            onClick={() => navigate("/settings#you")}
            data-testid="saves-back"
            className="reaction-pill mb-8 inline-flex"
          >
            <ArrowLeft size={12} /> Back to You
          </button>
          <div className="label mb-3 flex items-center gap-2">
            <BookMarked size={11} /> Saved items
          </div>
          <h1 className="serif text-4xl sm:text-5xl leading-tight">Everything you&apos;ve kept.</h1>
          <p className="serif italic text-lg text-muted-foreground mt-5 max-w-2xl">
            Concepts, Oracle correlations, and the threads you marked for later.
          </p>
        </header>

        {fetching ? (
          <p className="serif italic text-muted-foreground py-12 text-center">Gathering…</p>
        ) : items.length === 0 ? (
          <p className="serif italic text-muted-foreground py-12 text-center">
            Nothing here yet. Save anything that lands — concepts, Oracle responses, your own correlations.
          </p>
        ) : (
          <ul className="space-y-px bg-border">
            {items.map((it) => (
              <li
                key={it.save_id}
                data-testid={`save-row-${it.save_id}`}
                className="bg-card p-6"
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => openItem(it)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openItem(it); } }}
                  className="cursor-pointer transition-colors hover:opacity-90"
                >
                  <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {it.visibility === "public"
                        ? <Globe size={11} className="text-[hsl(var(--primary))]" />
                        : <Lock size={11} className="text-muted-foreground" />}
                      <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {it.kind === "concept" ? "concept" : it.kind === "submission_migrated" ? "correlation" : "oracle"}
                        {" · "}{it.created_at ? new Date(it.created_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                  </div>
                  <div className="serif text-xl text-foreground leading-snug">{it.title}</div>
                  {isRenderableCardData(it.raw?.card_data) ? (
                    // Card thumb carries the same distilled insight — no need
                    // to also list the bullets underneath.
                    null
                  ) : it.bullets && it.bullets.length > 0 ? (
                    <ul className="mt-3 space-y-1">
                      {it.bullets.slice(0, 4).map((b, i) => (
                        <li key={`${it.id}-b-${i}`} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                          <span className="text-[hsl(var(--primary))] mt-0.5">◇</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    it.preview && (
                      <p className="mt-3 text-sm text-muted-foreground italic line-clamp-3">{it.preview}</p>
                    )
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    {it.visibility === "public" && (
                      <span
                        data-testid={`save-previously-shared-${it.save_id}`}
                        title="This is public on the Weekly feed"
                        className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-[hsl(var(--primary)/0.5)] text-[hsl(var(--primary))] inline-flex items-center gap-1.5"
                      >
                        <Globe size={11} />
                        Previously shared
                      </span>
                    )}
                    <AddToCollectionMenu
                      kind={it.kind === "concept" ? "concept" : "correlation"}
                      itemId={it.kind === "concept" ? it.concept_id : it.save_id}
                      label="Add to collection"
                    />
                    {it.kind !== "concept" && (it.raw?.question || it.raw?.response) && (
                      <button
                        data-testid={`save-share-card-${it.save_id}`}
                        onClick={(ev) => { ev.stopPropagation(); openShareCard(it); }}
                        disabled={publishingId === it.save_id}
                        className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--primary))] inline-flex items-center gap-1.5 disabled:opacity-50"
                        title={
                          it.visibility === "public"
                            ? "Generate a vertical share card to post on Instagram, X, etc."
                            : "Publish this correlation to Weekly and generate a shareable card."
                        }
                      >
                        <Share2 size={11} />
                        {publishingId === it.save_id ? "Publishing…" : "Share as card"}
                      </button>
                    )}
                  </div>
                  <button
                    data-testid={`save-remove-${it.save_id}`}
                    onClick={(ev) => removeItem(it, ev)}
                    className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--destructive))]"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {modalItem && (
        <CorrelationModal
          item={modalItem}
          conceptLink={modalConceptLink}
          weeklyLink={modalWeeklyLink}
          onClose={() => { setModalItem(null); setModalConceptLink(null); setModalWeeklyLink(null); }}
        />
      )}
      {shareItem && (
        <ShareCardModal
          open={!!shareItem}
          onClose={() => setShareItem(null)}
          question={shareItem.raw?.question || shareItem.title || ""}
          response={shareItem.raw?.response || shareItem.preview || ""}
          saveId={shareItem.save_id}
        />
      )}
    </div>
  );
}
